/**
 * CheTuo Web — Dashboard page (门店概览).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { getDashboard } from '../../api/dashboard';
import { listMyShops, type ShopView } from '../../api/shop';
import { tokenStorage } from '../../api/client';

const STATE_LABELS: Record<string, string> = {
  trial: '试用中',
  active: '已激活',
  readonly: '只读模式',
  expired: '已过期',
};

const STATUS_VARIANTS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  open: 'default',
  in_progress: 'primary',
  done: 'warning',
  paid: 'success',
  unpaid: 'danger',
  cancelled: 'default',
};

export function DashboardPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: shops, isLoading: isShopsLoading } = useQuery({
    queryKey: ['shops'],
    queryFn: listMyShops,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    enabled: !!tokenStorage.getShopId(),
  });

  const currentShopId = tokenStorage.getShopId();
  const shop: ShopView | undefined = shops?.find((s) => s.id === currentShopId) ?? shops?.[0];

  useEffect(() => {
    if (shop && shop.id !== currentShopId) {
      tokenStorage.setShopId(shop.id);
    }
  }, [shop, currentShopId]);

  if (isShopsLoading || isLoading) {
    return <div className="py-12 text-center text-gray-500">加载中...</div>;
  }

  if (!data) {
    return <div className="py-12 text-center text-gray-500">暂无门店数据</div>;
  }

  const stats = [
    { label: '今日工单', value: data.todayOrderCount, suffix: '单', color: 'text-gray-900' },
    { label: '施工中', value: data.inProgressCount, suffix: '单', color: 'text-blue-600' },
    { label: '待收款', value: data.pendingPaymentCount, suffix: '单', color: 'text-yellow-600' },
    { label: '今日营收', value: `¥${data.todayRevenue}`, suffix: '', color: 'text-green-600' },
    { label: '客户总数', value: data.customerCount, suffix: '位', color: 'text-gray-900' },
    { label: '挂账金额', value: `¥${data.unpaidTotal}`, suffix: '', color: 'text-red-600' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">门店概览</h1>
          <p className="text-sm text-gray-500">{data.shop.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={data.shop.trialState === 'active' ? 'success' : data.shop.trialState === 'trial' ? 'warning' : 'danger'}>
            {STATE_LABELS[data.shop.trialState] ?? data.shop.trialState}
          </Badge>
          {data.shop.trialState === 'trial' && (
            <span className="text-sm text-gray-500">剩 {data.shop.remainingTrialDays} 天</span>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((s) => (
          <Card key={s.label} className="flex flex-col justify-between">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500">{s.label}</p>
              <p className={`mt-1 text-2xl font-bold ${s.color}`}>
                {s.value}
                {s.suffix && <span className="ml-1 text-sm font-medium text-gray-400">{s.suffix}</span>}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>今日工单</CardTitle>
          </CardHeader>
          <CardContent>
            {!data.todayOrders.length ? (
              <p className="py-8 text-center text-sm text-gray-500">今日暂无工单</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.todayOrders.slice(0, 5).map((order) => (
                  <button
                    key={order.id}
                    onClick={() => navigate(`/shop-admin/orders/${order.id}`)}
                    className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-blue-600"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{order.vehiclePlate}</p>
                      <p className="text-sm text-gray-500">{order.customerName} · {order.serviceSummary}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">¥{order.totalAmount}</p>
                      <Badge variant={STATUS_VARIANTS[order.status] ?? 'default'}>
                        {order.status}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>快捷操作</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Button className="w-full" onClick={() => navigate('/shop-admin/orders/new')}>
                + 新建工单
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/shop-admin/customers')}>
                客户车辆
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/shop-admin/payment-flow')}>
                收款流水
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/shop-admin/settings')}>
                门店设置
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
